import {
    ReactElement,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useImperativeHandle,
    useMemo
} from 'react';
import {
    Team,
    UpdateContactsSchedule,
    ContactsSchedule,
    UpdateContactsScheduleDay
} from '@/api';
import { useUpdateTeamAction } from '../actions';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISwitch,
    UIText,
    UIFormInput,
    UICheckbox,
    UISelectInput,
    UISelectValue
} from '@/ui';
import { getTimezoneOptions } from '@/common/util';
import {
    DayOfWeek,
    DAYS_OF_WEEK,
    minutesToTimeString,
    minutesToTimeString12,
    timeStringToMinutes
} from '../util/out-of-hours-schedule';
import { useTimeSettings } from '@/common/hooks';
import { useCompaniesTranslation } from '../hooks';

type TeamContactsScheduleSettingsSectionProps = { team: Team };

export function TeamContactsScheduleSettingsSection(
    props: TeamContactsScheduleSettingsSectionProps
): ReactElement {
    const { team } = props;

    const formRef = useRef<TeamContactsScheduleSettingsSectionFormRef>(null);
    const updateTeam = useUpdateTeamAction({ teamId: team.id });

    const [isEditing, setIsEditing] = useState(false);

    function onSubmit(data: {
        contactsSchedule: UpdateContactsSchedule | null;
        timezone: string;
    }) {
        const { contactsSchedule, timezone } = data;

        updateTeam
            .updateTeam({
                contactsSchedule,
                timezone
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <TeamContactsScheduleSettingsSectionForm
            canEdit={updateTeam.isAllowed}
            ref={formRef}
            initialContactsSchedule={team.contactsSchedule}
            initialTimezone={team.timezone}
            loading={updateTeam.loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSubmit={onSubmit}
        />
    );
}

const DEFAULT_CONTACTS_SCHEDULE: UpdateContactsSchedule = {
    monday: {
        isWorkingDay: true,
        allDay: false,
        startTime: 540,
        endTime: 1020
    },
    tuesday: {
        isWorkingDay: true,
        allDay: false,
        startTime: 540,
        endTime: 1020
    },
    wednesday: {
        isWorkingDay: true,
        allDay: false,
        startTime: 540,
        endTime: 1020
    },
    thursday: {
        isWorkingDay: true,
        allDay: false,
        startTime: 540,
        endTime: 1020
    },
    friday: {
        isWorkingDay: true,
        allDay: false,
        startTime: 540,
        endTime: 1020
    },
    saturday: {
        isWorkingDay: false
    },
    sunday: {
        isWorkingDay: false
    }
};

type TeamContactsScheduleSettingsSectionFormProps = {
    canEdit: boolean;
    initialContactsSchedule: ContactsSchedule | null;
    initialTimezone: string;
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: {
        contactsSchedule: UpdateContactsSchedule | null;
        timezone: string;
    }) => void;
};

type TeamContactsScheduleSettingsSectionFormRef = {
    resetForm: () => void;
};
const TeamContactsScheduleSettingsSectionForm = forwardRef<
    TeamContactsScheduleSettingsSectionFormRef,
    TeamContactsScheduleSettingsSectionFormProps
>((props, ref): ReactElement => {
    const { t } = useCompaniesTranslation();
    const {
        canEdit,
        initialContactsSchedule,
        initialTimezone,
        loading,
        isEditing,
        setIsEditing,
        onSubmit
    } = props;

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Timezone state
     */
    const [timezone, setTimezone] = useState(initialTimezone);
    const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
    const selectedTimezoneOption = useMemo((): UISelectValue | undefined => {
        return timezoneOptions.find((opt) => opt.value === timezone);
    }, [timezoneOptions, timezone]);

    /**
     * Enabled state
     */
    const [contactsScheduleEnabled, setContactsScheduleEnabled] =
        useState<boolean>(initialContactsSchedule != null);

    /**
     * Advanced config state
     */
    const [contactsSchedule, setContactsSchedule] =
        useState<UpdateContactsSchedule>(
            initialContactsSchedule ?? DEFAULT_CONTACTS_SCHEDULE
        );
    const checkContactsSchedule = useCallback(
        (
            contactsSchedule: UpdateContactsSchedule
        ): Record<string, string> | null => {
            const errors: Record<string, string> = {};
            let hasError = false;
            DAYS_OF_WEEK.forEach(({ key }) => {
                const dayConfig = contactsSchedule[key];
                if (dayConfig.isWorkingDay && !dayConfig.allDay) {
                    if (
                        dayConfig.startTime == undefined ||
                        isNaN(dayConfig.startTime)
                    ) {
                        errors[`${key}-startTime`] = '1';
                        hasError = true;
                    }
                    if (
                        dayConfig.endTime == undefined ||
                        isNaN(dayConfig.endTime)
                    ) {
                        errors[`${key}-endTime`] = '1';
                        hasError = true;
                    }
                    // Check if endTime is less than or equal to startTime
                    if (
                        dayConfig.startTime != undefined &&
                        !isNaN(dayConfig.startTime) &&
                        dayConfig.endTime != undefined &&
                        !isNaN(dayConfig.endTime) &&
                        dayConfig.endTime <= dayConfig.startTime
                    ) {
                        errors[`${key}-endTime`] = '2';
                        hasError = true;
                    }
                }
            });
            if (hasError) {
                errors['general'] = t(
                    'components.team_contacts_schedule_settings_section.error_invalid_times'
                );
                return errors;
            }
            return null;
        },
        [t]
    );
    const contactsScheduleError = useMemo(() => {
        if (!showRealTimeErrors) {
            return null;
        }
        return checkContactsSchedule(contactsSchedule);
    }, [contactsSchedule, showRealTimeErrors, checkContactsSchedule]);
    const updateContactsSchedule = useCallback(
        (day: DayOfWeek, updates: Partial<UpdateContactsScheduleDay>) => {
            setContactsSchedule((prev) => ({
                ...prev,
                [day]: { ...prev[day], ...updates }
            }));
        },
        []
    );

    /**
     * Form submission
     */
    function submitForm() {
        if (contactsScheduleEnabled) {
            function sanitizeContactsSchedule(
                schedule: UpdateContactsSchedule
            ): UpdateContactsSchedule {
                const sanitizeDay = (
                    day: UpdateContactsScheduleDay
                ): UpdateContactsScheduleDay => {
                    if (!day.isWorkingDay || day.allDay) {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { startTime, endTime, ...rest } = day;
                        return rest;
                    }
                    return day;
                };

                return {
                    monday: sanitizeDay(schedule.monday),
                    tuesday: sanitizeDay(schedule.tuesday),
                    wednesday: sanitizeDay(schedule.wednesday),
                    thursday: sanitizeDay(schedule.thursday),
                    friday: sanitizeDay(schedule.friday),
                    saturday: sanitizeDay(schedule.saturday),
                    sunday: sanitizeDay(schedule.sunday)
                };
            }

            onSubmit({
                contactsSchedule: sanitizeContactsSchedule(contactsSchedule),
                timezone
            });
        } else {
            onSubmit({ contactsSchedule: null, timezone });
        }
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        setShowRealTimeErrors(true);

        if (!checkContactsSchedule(contactsSchedule)) {
            setShowRealTimeErrors(false);
            submitForm();
        }
    };

    function resetForm() {
        setContactsScheduleEnabled(initialContactsSchedule != null);
        setContactsSchedule(
            initialContactsSchedule ?? DEFAULT_CONTACTS_SCHEDULE
        );
        setTimezone(initialTimezone);
        setShowRealTimeErrors(false);
        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const sectionTitle = t(
        'components.team_contacts_schedule_settings_section.title'
    );

    return (
        <>
            <UISection>
                {isEditing ? (
                    <EditableForm
                        sectionTitle={sectionTitle}
                        timezone={timezone}
                        setTimezone={setTimezone}
                        timezoneOptions={timezoneOptions}
                        selectedTimezoneOption={selectedTimezoneOption}
                        contactsScheduleEnabled={contactsScheduleEnabled}
                        setContactsScheduleEnabled={setContactsScheduleEnabled}
                        contactsSchedule={contactsSchedule}
                        contactsScheduleError={contactsScheduleError}
                        updateContactsSchedule={updateContactsSchedule}
                        loading={loading}
                        canEdit={canEdit}
                        resetForm={resetForm}
                        handleSubmit={handleSubmit}
                    />
                ) : (
                    <NonEditableForm
                        sectionTitle={sectionTitle}
                        timezone={timezone}
                        timezoneOptions={timezoneOptions}
                        contactsScheduleEnabled={contactsScheduleEnabled}
                        contactsSchedule={contactsSchedule}
                        onEditClick={() => {
                            setIsEditing(true);
                            setShowRealTimeErrors(false);
                        }}
                        canEdit={canEdit}
                    />
                )}
            </UISection>
        </>
    );
});

function NonEditableForm(props: {
    sectionTitle: string;
    timezone: string;
    timezoneOptions: UISelectValue[];
    contactsScheduleEnabled: boolean;
    contactsSchedule: UpdateContactsSchedule;
    onEditClick: () => void;
    canEdit: boolean;
}): ReactElement {
    const {
        sectionTitle,
        timezone,
        timezoneOptions,
        contactsScheduleEnabled,
        contactsSchedule,
        onEditClick,
        canEdit
    } = props;

    const { t } = useCompaniesTranslation();

    const { use24HourClock } = useTimeSettings();
    const formatTime = use24HourClock
        ? minutesToTimeString
        : minutesToTimeString12;

    const timezoneDisplayLabel = useMemo(() => {
        const option = timezoneOptions.find((opt) => opt.value === timezone);
        return option?.label ?? timezone;
    }, [timezoneOptions, timezone]);

    return (
        <>
            <UISectionHeader
                title={sectionTitle}
                rightButtons={
                    canEdit
                        ? [
                              {
                                  text: t('shared.labels.edit'),
                                  variant: 'textDefault',
                                  type: 'button',
                                  onClick: onEditClick
                              }
                          ]
                        : []
                }
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <OutOfHoursDescription />

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>{t('shared.labels.timezone')}</UIText>
                            <UIText type="default2">
                                {t(
                                    'components.team_contacts_schedule_settings_section.timezone_description'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UIText medium>{timezoneDisplayLabel}</UIText>
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8
                        }}
                    >
                        {!contactsScheduleEnabled && (
                            <UIText medium type="error">
                                {t('shared.labels.disabled')}
                            </UIText>
                        )}
                        {contactsScheduleEnabled && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    marginTop: 8
                                }}
                            >
                                {DAYS_OF_WEEK.map(({ key, label }) => {
                                    const dayConfig = contactsSchedule[key];
                                    return (
                                        <div
                                            key={key}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                gap: 8,
                                                alignItems: 'center',
                                                opacity: dayConfig.isWorkingDay
                                                    ? 1
                                                    : 0.5
                                            }}
                                        >
                                            <UIText
                                                medium
                                                style={{
                                                    minWidth: 100
                                                }}
                                            >
                                                {label}
                                            </UIText>
                                            <UIText type="default2">
                                                {!dayConfig.isWorkingDay
                                                    ? t(
                                                          'components.team_contacts_schedule_settings_section.day_unavailable'
                                                      )
                                                    : dayConfig.allDay
                                                      ? t(
                                                            'shared.labels.all_day'
                                                        )
                                                      : `${formatTime(dayConfig.startTime ?? 0, '17:00')} - ${formatTime(dayConfig.endTime ?? 0, '09:00')}`}
                                            </UIText>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </UISectionContent>
        </>
    );
}

function DayScheduleRow(props: {
    label: string;
    dayConfig: UpdateContactsScheduleDay;
    startTimeError: boolean;
    endTimeError: boolean;
    onUpdate: (updates: Partial<UpdateContactsScheduleDay>) => void;
    canEdit: boolean;
}): ReactElement {
    const {
        label,
        dayConfig,
        startTimeError,
        endTimeError,
        onUpdate,
        canEdit
    } = props;

    const { t } = useCompaniesTranslation();
    const isDisabled = !canEdit || !dayConfig.isWorkingDay;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 16,
                padding: '8px 0',
                borderBottom: '1px solid var(--color-border-light)'
            }}
        >
            {/* Day enabled checkbox */}
            <UICheckbox
                checked={dayConfig.isWorkingDay}
                onChange={(checked) => onUpdate({ isWorkingDay: checked })}
                disabled={!canEdit}
            />

            {/* Day name */}
            <UIText
                medium
                style={{
                    width: 100,
                    opacity: dayConfig.isWorkingDay ? 1 : 0.5
                }}
            >
                {label}
            </UIText>

            {/* All day checkbox */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: 100,
                    opacity: dayConfig.isWorkingDay ? 1 : 0.5
                }}
            >
                <UICheckbox
                    checked={dayConfig.allDay}
                    onChange={(checked) => onUpdate({ allDay: checked })}
                    disabled={isDisabled}
                />
                <UIText type="default2">{t('shared.labels.all_day')}</UIText>
            </div>

            {/* Time inputs - disabled when all day is checked or day is disabled */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 8,
                    alignItems: 'center',
                    opacity:
                        !dayConfig.isWorkingDay || dayConfig.allDay ? 0.5 : 1
                }}
            >
                <UIFormInput
                    type="time"
                    value={minutesToTimeString(
                        dayConfig.startTime ?? NaN,
                        '17:00'
                    )}
                    onChange={(value) =>
                        onUpdate({ startTime: timeStringToMinutes(value) })
                    }
                    disabled={isDisabled || dayConfig.allDay}
                    errorHighlight={startTimeError}
                    style={{ width: 110 }}
                />
                <UIText type="default2">to</UIText>
                <UIFormInput
                    type="time"
                    value={minutesToTimeString(
                        dayConfig.endTime ?? NaN,
                        '09:00'
                    )}
                    onChange={(value) =>
                        onUpdate({ endTime: timeStringToMinutes(value) })
                    }
                    disabled={isDisabled || dayConfig.allDay}
                    errorHighlight={endTimeError}
                    style={{ width: 110 }}
                />
            </div>
        </div>
    );
}

function EditableForm(props: {
    sectionTitle: string;
    timezone: string;
    setTimezone: (value: string) => void;
    timezoneOptions: UISelectValue[];
    selectedTimezoneOption: UISelectValue | undefined;
    contactsScheduleEnabled: boolean;
    setContactsScheduleEnabled: Dispatch<SetStateAction<boolean>>;
    contactsSchedule: UpdateContactsSchedule;
    contactsScheduleError: Record<string, string> | null;
    updateContactsSchedule: (
        day: DayOfWeek,
        updates: Partial<UpdateContactsScheduleDay>
    ) => void;
    loading: boolean;
    canEdit: boolean;
    resetForm: () => void;
    handleSubmit: FormEventHandler;
}): ReactElement {
    const {
        sectionTitle,
        setTimezone,
        timezoneOptions,
        selectedTimezoneOption,
        contactsScheduleEnabled,
        setContactsScheduleEnabled,
        contactsSchedule,
        contactsScheduleError,
        updateContactsSchedule,
        loading,
        canEdit,
        resetForm,
        handleSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UISectionHeader title={sectionTitle} />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <OutOfHoursDescription />

                    {/* Timezone selector */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>{t('shared.labels.timezone')}</UIText>
                            <UIText type="default2">
                                {t(
                                    'components.team_contacts_schedule_settings_section.timezone_description'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UISelectInput
                                value={selectedTimezoneOption}
                                options={timezoneOptions}
                                onChange={(option) => {
                                    if (option) {
                                        setTimezone(option.value);
                                    }
                                }}
                                disabled={!canEdit}
                                clearable={false}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    {/* Toggle for Out of Hours Enabled */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.team_contacts_schedule_settings_section.enable_label'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.team_contacts_schedule_settings_section.enable_description'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UISwitch
                                checked={contactsScheduleEnabled}
                                onChange={setContactsScheduleEnabled}
                                disabled={!canEdit}
                            />
                        </div>
                    </div>

                    {/* Schedule configuration - only show when enabled */}
                    {contactsScheduleEnabled && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0
                                }}
                            >
                                {DAYS_OF_WEEK.map(({ key, label }) => (
                                    <DayScheduleRow
                                        key={key}
                                        label={label}
                                        dayConfig={contactsSchedule[key]}
                                        startTimeError={
                                            contactsScheduleError
                                                ? !!contactsScheduleError[
                                                      `${key}-startTime`
                                                  ]
                                                : false
                                        }
                                        endTimeError={
                                            contactsScheduleError
                                                ? !!contactsScheduleError[
                                                      `${key}-endTime`
                                                  ]
                                                : false
                                        }
                                        onUpdate={(updates) =>
                                            updateContactsSchedule(key, updates)
                                        }
                                        canEdit={canEdit}
                                    />
                                ))}
                            </div>

                            {contactsScheduleError ? (
                                <UIText type="error">
                                    {contactsScheduleError['general']}
                                </UIText>
                            ) : (
                                <UIText type="default2">
                                    {t(
                                        'components.team_contacts_schedule_settings_section.days_description'
                                    )}
                                </UIText>
                            )}
                        </div>
                    )}
                </div>
            </UISectionContent>
            <UISectionFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: loading
                            ? t('shared.labels.saving')
                            : t('shared.labels.save')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: resetForm
                    }
                ]}
            />
        </form>
    );
}

function OutOfHoursDescription(): ReactElement {
    const { t } = useCompaniesTranslation();
    return (
        <UIText type="default2">
            {t('components.team_contacts_schedule_settings_section.explainer')}
        </UIText>
    );
}
